/**
 * Closeout unified document editor.
 *
 * ONE editor that replaces the three old closeout tools (per-field override
 * side-panel, the mapping "fix a field" card, and free-form markup). It renders
 * a generated document (work order / test report) as page images and lets the
 * office:
 *   • click a MAPPED FIELD right where it sits on the page and correct its value
 *     (type into a text/date field, toggle a Y/N box) — an "edited" ring shows,
 *     and there's a one-tap Revert;
 *   • drop free-form TEXT / CHECK / CIRCLE / WHITE-OUT marks anywhere, then
 *     drag/resize/delete them.
 * One Save posts both — field value edits AND free-form marks — to
 * POST /close-out/jobs/:jobId/documents/:attachmentId/edit-document, which
 * regenerates the document from the corrected fields ONCE and then bakes the
 * marks on top. The document locks in as the copy that rides the packet.
 *
 * Same normalized 0..1 top-left overlay convention as the server bake
 * (`workOrderPdfEditor.ts`) and the fill engine's field positions
 * (`normalizedTopLeftRect`, fillEngine.ts) so a field's clickable box lands
 * exactly where the value prints.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Type, CheckSquare, Circle as CircleIcon, Eraser, Loader2, X, Trash2, Move, RotateCcw } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import { api, fetchAuthedBlob } from "../lib/api";

type MarkType = "text" | "checkbox" | "circle" | "whiteout";

interface Mark {
    id: string;
    page: number; // 1-indexed
    type: MarkType;
    xPct: number;
    yPct: number;
    wPct: number;
    hPct: number;
    value?: string;
    checked?: boolean;
}

/** A mapped template field the office can correct in place (from /fields). */
export interface EditableField {
    id: string;
    label: string;
    pdf_field_name: string | null;
    field_type: string;
    mapping_type: string;
    page_number: number;
    is_repeating?: boolean;
    checked?: boolean | null;
    resolved_value?: string | null;
    override_value?: string | null;
    position_json?: { x: number; y: number; width?: number | null; height?: number | null } | null;
    // Per-device instance (Issue #355): a repeating field expanded to one entry
    // per device. `id` is the synthetic `${templateFieldId}::${assetId}`, and
    // `device_label` (serial → "Device N") names the device in the box tooltip so
    // "device 3's Operable" is unambiguous. Absent on ordinary fields.
    device_asset_id?: string | null;
    device_index?: number | null;
    device_label?: string | null;
}

interface RenderedPage {
    pageNum: number;
    src: string;
    /** Intrinsic PDF-point dimensions (page.getSize() on the server) — used to
        convert a field's position_json into normalized top-left pct. */
    w: number;
    h: number;
}

/** A field placed on a page, with its normalized top-left rect. */
interface PlacedField {
    field: EditableField;
    xPct: number;
    yPct: number;
    wPct: number;
    hPct: number;
}

const TOOLS: { type: MarkType; label: string; icon: typeof Type }[] = [
    { type: "text", label: "Text", icon: Type },
    { type: "checkbox", label: "Check", icon: CheckSquare },
    { type: "circle", label: "Circle", icon: CircleIcon },
    { type: "whiteout", label: "White-out", icon: Eraser },
];

const DEFAULT_SIZE: Record<MarkType, { w: number; h: number }> = {
    text: { w: 0.26, h: 0.028 },
    checkbox: { w: 0.03, h: 0.022 },
    circle: { w: 0.16, h: 0.08 },
    whiteout: { w: 0.2, h: 0.03 },
};

const TOGGLE_FIELD_TYPES = new Set(["checkbox", "radio", "yes_no"]);
const isToggleField = (f: EditableField) => f.mapping_type === "checkbox_match" || TOGGLE_FIELD_TYPES.has(f.field_type);
const isEditableFieldType = (f: EditableField) => f.field_type !== "signature" && f.field_type !== "image";
/** Box label, with the device name appended for a per-device instance so
    "Operable · Device 3" is unambiguous (Issue #355). */
const fieldDisplayLabel = (f: EditableField) => {
    const base = f.label || f.pdf_field_name || "field";
    return f.device_label ? `${base} · ${f.device_label}` : base;
};
const isTruthy = (v: string) => ["true", "1", "yes", "x", "checked", "on"].includes(v.trim().toLowerCase());

/** The committed value for a field as a normalized string ("true"/"false" for
    toggles) — override wins over the resolved value. Staged edits compare to
    this so an unchanged field never triggers a needless regenerate. */
function committedString(f: EditableField): string {
    if (isToggleField(f)) {
        const on = f.override_value != null ? isTruthy(f.override_value) : !!f.checked;
        return on ? "true" : "false";
    }
    return f.override_value ?? f.resolved_value ?? "";
}

/** Convert a field's position_json (PDF points, bottom-left origin) to a
    normalized top-left rect. Mirrors normalizedTopLeftRect (fillEngine.ts). */
function fieldRect(
    pos: { x: number; y: number; width?: number | null; height?: number | null },
    pageW: number,
    pageH: number,
): { xPct: number; yPct: number; wPct: number; hPct: number } | null {
    if (!pageW || !pageH) return null;
    const rawW = pos.width ?? 100;
    const rawH = pos.height ?? 14;
    const w = Math.abs(rawW);
    const h = Math.abs(rawH);
    const x = rawW < 0 ? pos.x + rawW : pos.x;
    const yBottom = rawH < 0 ? pos.y + rawH : pos.y;
    return {
        xPct: x / pageW,
        yPct: (pageH - yBottom - h) / pageH,
        wPct: w / pageW,
        hPct: h / pageH,
    };
}

function uid(): string {
    return Math.random().toString(36).slice(2, 10);
}

export function CloseoutPdfMarkup({
    jobId,
    attachmentId,
    pdfUrl,
    title,
    fields = [],
    onClose,
    onSaved,
}: {
    jobId: string;
    attachmentId: string;
    /** Authed URL to fetch the current document's PDF bytes. */
    pdfUrl: string;
    title?: string;
    /** Mapped fields for this document (from the doc-fields query). Empty for
        agency PDFs / documents with no GWC mapping — then only marks show. */
    fields?: EditableField[];
    onClose: () => void;
    onSaved: () => void;
}) {
    const [pages, setPages] = useState<RenderedPage[]>([]);
    const [marks, setMarks] = useState<Mark[]>([]);
    const [tool, setTool] = useState<MarkType | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    // Staged per-field value edits: fieldId → new value, or null to revert the
    // field to its resolved value. Absent = untouched.
    const [fieldEdits, setFieldEdits] = useState<Record<string, string | null>>({});
    const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // Clickable-in-place fields: non-signature/image, with a known position, and
    // either non-repeating OR a per-device instance (Issue #355) — a repeating
    // field expanded to one device carries its own page + asset, so device 3's
    // Y/N grid is now editable, not read-only. A bare repeating field (no device
    // key) stays non-placeable.
    const placeableFields = useMemo(
        () =>
            fields.filter((f) => isEditableFieldType(f) && f.position_json && (!f.is_repeating || !!f.device_asset_id)),
        [fields],
    );
    const fieldsById = useMemo(() => new Map(fields.map((f) => [f.id, f])), [fields]);

    // ── Render the current PDF to page images ────────────────────────────
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const blob = await fetchAuthedBlob(pdfUrl);
                const bytes = new Uint8Array(await blob.arrayBuffer());
                if (cancelled) return;
                const [pdfjsMod, workerMod] = await Promise.all([
                    import("pdfjs-dist/legacy/build/pdf.mjs"),
                    import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker"),
                ]);
                if (cancelled) return;
                const pdfjs = pdfjsMod;
                pdfjs.GlobalWorkerOptions.workerPort = new workerMod.default();
                const doc = await pdfjs.getDocument({ data: bytes }).promise;
                const dpr = Math.min(window.devicePixelRatio || 1, 2);
                const rendered: RenderedPage[] = [];
                for (let n = 1; n <= doc.numPages; n++) {
                    if (cancelled) return;
                    const page = await doc.getPage(n);
                    const base = page.getViewport({ scale: 1 });
                    const renderScale = Math.min(2.5, Math.max(1, (1100 / base.width) * dpr));
                    const viewport = page.getViewport({ scale: renderScale });
                    const canvas = document.createElement("canvas");
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    const ctx = canvas.getContext("2d");
                    if (!ctx) continue;
                    await page.render({ canvasContext: ctx, viewport, canvas }).promise;
                    rendered.push({ pageNum: n, src: canvas.toDataURL("image/png"), w: base.width, h: base.height });
                }
                if (cancelled) return;
                setPages(rendered);
                setLoading(false);
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : "Couldn't open the document.");
                setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [pdfUrl]);

    const placeMark = useCallback(
        (page: number, xPct: number, yPct: number) => {
            if (!tool) return;
            const size = DEFAULT_SIZE[tool];
            const m: Mark = {
                id: uid(),
                page,
                type: tool,
                xPct: Math.max(0, Math.min(1 - size.w, xPct - size.w / 2)),
                yPct: Math.max(0, Math.min(1 - size.h, yPct - size.h / 2)),
                wPct: size.w,
                hPct: size.h,
                value: tool === "text" ? "" : undefined,
                checked: tool === "checkbox" ? true : undefined,
            };
            setMarks((prev) => [...prev, m]);
            setSelectedId(m.id);
            setTool(null);
        },
        [tool],
    );

    const updateMark = useCallback((id: string, patch: Partial<Mark>) => {
        setMarks((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    }, []);
    const deleteMark = useCallback((id: string) => {
        setMarks((prev) => prev.filter((m) => m.id !== id));
        setSelectedId((cur) => (cur === id ? null : cur));
    }, []);

    // Stage a field value edit. `null` reverts to the resolved value.
    const stageFieldEdit = useCallback((fieldId: string, value: string | null) => {
        setFieldEdits((prev) => ({ ...prev, [fieldId]: value }));
    }, []);
    const clearFieldStage = useCallback((fieldId: string) => {
        setFieldEdits((prev) => {
            const n = { ...prev };
            delete n[fieldId];
            return n;
        });
    }, []);

    // The staged value shown for a field (staged edit wins; else committed).
    const shownValue = useCallback(
        (f: EditableField): string => {
            const staged = fieldEdits[f.id];
            if (staged === undefined) return committedString(f);
            if (staged === null) return committedString({ ...f, override_value: null }); // reverted → resolved
            return staged;
        },
        [fieldEdits],
    );
    // Does this field carry an edit relative to what currently prints?
    const fieldIsEdited = useCallback(
        (f: EditableField): boolean => {
            const staged = fieldEdits[f.id];
            if (staged !== undefined) {
                if (staged === null) return false; // reverting clears the "edited" look
                return staged !== committedString(f);
            }
            return f.override_value != null;
        },
        [fieldEdits],
    );

    // Which fields actually change on save (differ from what prints now).
    const changedOverrides = useMemo(() => {
        const out: { fieldId: string; value: string | null }[] = [];
        for (const [fieldId, val] of Object.entries(fieldEdits)) {
            const f = fieldsById.get(fieldId);
            if (!f) continue;
            if (val === null) {
                if (f.override_value != null) out.push({ fieldId, value: null }); // revert an existing override
            } else if (val !== committedString(f)) {
                out.push({ fieldId, value: val });
            }
        }
        return out;
    }, [fieldEdits, fieldsById]);

    const hasChanges = changedOverrides.length > 0 || marks.length > 0;

    const handleSave = useCallback(async () => {
        if (!hasChanges) {
            toast.error("Edit a field or place a mark first.");
            return;
        }
        setSaving(true);
        try {
            const res = await api.post<{ ok: boolean; reason?: string; accepted?: boolean }>(
                `/api/close-out/jobs/${jobId}/documents/${attachmentId}/edit-document`,
                {
                    fieldOverrides: changedOverrides,
                    marks: marks.map((m) => ({
                        page: m.page,
                        type: m.type,
                        xPct: m.xPct,
                        yPct: m.yPct,
                        wPct: m.wPct,
                        hPct: m.hPct,
                        value: m.value ?? null,
                        checked: m.checked ?? false,
                    })),
                    images: {},
                },
            );
            if (res?.ok === false) {
                toast.error(res.reason ?? "Couldn't save your edits.");
                setSaving(false);
                return;
            }
            toast.success(
                res?.accepted ? "Saved — this document is corrected & accepted ✓" : "Saved — document updated.",
            );
            onSaved();
            onClose();
        } catch (e) {
            toast.error((e as { body?: { message?: string } })?.body?.message ?? "Couldn't save your edits.");
            setSaving(false);
        }
    }, [hasChanges, changedOverrides, marks, jobId, attachmentId, onSaved, onClose]);

    return (
        <div className="fixed inset-0 z-50 flex flex-col bg-background">
            {/* Header + toolbar */}
            <div className="sticky top-0 z-10 border-b bg-card">
                <div className="flex items-center gap-2 px-3 py-2">
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold">Edit document</div>
                        <div className="truncate text-xs text-foreground/60">{title ?? "Generated document"}</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
                        <X className="mr-1 h-4 w-4" /> Cancel
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={saving || !hasChanges}>
                        {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                        Save
                    </Button>
                </div>
                {!error && (
                    <div className="flex gap-1 overflow-x-auto border-t px-2 py-2">
                        {TOOLS.map((t) => {
                            const Icon = t.icon;
                            return (
                                <button
                                    key={t.type}
                                    type="button"
                                    onClick={() => setTool((cur) => (cur === t.type ? null : t.type))}
                                    className={cn(
                                        "flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
                                        tool === t.type
                                            ? "bg-primary text-primary-foreground"
                                            : "bg-muted text-foreground/80 hover:bg-muted/70",
                                    )}
                                >
                                    <Icon className="h-3.5 w-3.5" /> {t.label}
                                </button>
                            );
                        })}
                        <span className="ml-auto self-center pr-1 text-[11px] text-muted-foreground">
                            {tool
                                ? `Tap the document to place a ${tool === "whiteout" ? "white-out" : tool}`
                                : placeableFields.length > 0
                                  ? "Tap a field to edit it · or pick a tool to add a mark"
                                  : "Pick a tool, then tap the document"}
                        </span>
                    </div>
                )}
            </div>

            {/* Pages */}
            <div className="flex-1 overflow-auto bg-muted/40 p-3">
                {loading ? (
                    <div className="flex h-full items-center justify-center text-sm text-foreground/60">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Opening document…
                    </div>
                ) : error ? (
                    <div className="p-8 text-center text-sm text-destructive">{error}</div>
                ) : (
                    <div className="mx-auto max-w-3xl space-y-3">
                        {pages.map((page) => {
                            const placed: PlacedField[] = placeableFields
                                .filter((f) => f.page_number === page.pageNum)
                                .map((f) => {
                                    const r = fieldRect(f.position_json!, page.w, page.h);
                                    return r ? { field: f, ...r } : null;
                                })
                                .filter((p): p is PlacedField => p !== null);
                            return (
                                <PageLayer
                                    key={page.pageNum}
                                    page={page}
                                    fields={placed}
                                    marks={marks.filter((m) => m.page === page.pageNum)}
                                    selectedId={selectedId}
                                    activeFieldId={activeFieldId}
                                    placing={!!tool}
                                    shownValue={shownValue}
                                    fieldIsEdited={fieldIsEdited}
                                    onPlace={(x, y) => placeMark(page.pageNum, x, y)}
                                    onSelect={setSelectedId}
                                    onUpdate={updateMark}
                                    onDelete={deleteMark}
                                    onActivateField={setActiveFieldId}
                                    onStageField={stageFieldEdit}
                                    onClearFieldStage={clearFieldStage}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── One page image + its field + mark overlay layer ────────────────────
function PageLayer({
    page,
    fields,
    marks,
    selectedId,
    activeFieldId,
    placing,
    shownValue,
    fieldIsEdited,
    onPlace,
    onSelect,
    onUpdate,
    onDelete,
    onActivateField,
    onStageField,
    onClearFieldStage,
}: {
    page: RenderedPage;
    fields: PlacedField[];
    marks: Mark[];
    selectedId: string | null;
    activeFieldId: string | null;
    placing: boolean;
    shownValue: (f: EditableField) => string;
    fieldIsEdited: (f: EditableField) => boolean;
    onPlace: (xPct: number, yPct: number) => void;
    onSelect: (id: string | null) => void;
    onUpdate: (id: string, patch: Partial<Mark>) => void;
    onDelete: (id: string) => void;
    onActivateField: (id: string | null) => void;
    onStageField: (fieldId: string, value: string | null) => void;
    onClearFieldStage: (fieldId: string) => void;
}) {
    const layerRef = useRef<HTMLDivElement>(null);

    const handlePointerDown = (e: React.PointerEvent) => {
        if (!placing) {
            if (e.target === layerRef.current) {
                onSelect(null);
                onActivateField(null);
            }
            return;
        }
        const rect = layerRef.current?.getBoundingClientRect();
        if (!rect) return;
        onPlace((e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height);
    };

    return (
        <div className="relative bg-white shadow">
            <img src={page.src} alt={`Page ${page.pageNum}`} className="block w-full select-none" draggable={false} />
            <div
                ref={layerRef}
                className={cn("absolute inset-0", placing ? "cursor-crosshair" : "cursor-default")}
                onPointerDown={handlePointerDown}
            >
                {/* Field boxes — inert while a mark tool is armed so taps place
                    marks instead of opening a field editor. */}
                {fields.map((pf) => (
                    <FieldBox
                        key={pf.field.id}
                        placed={pf}
                        active={activeFieldId === pf.field.id}
                        edited={fieldIsEdited(pf.field)}
                        value={shownValue(pf.field)}
                        inert={placing}
                        onActivate={() => onActivateField(pf.field.id)}
                        onDeactivate={() => onActivateField(null)}
                        onStage={(v) => onStageField(pf.field.id, v)}
                        onRevert={() => onClearFieldStage(pf.field.id)}
                    />
                ))}
                {marks.map((m) => (
                    <MarkBox
                        key={m.id}
                        mark={m}
                        selected={selectedId === m.id}
                        layerRef={layerRef}
                        onSelect={() => onSelect(m.id)}
                        onUpdate={(patch) => onUpdate(m.id, patch)}
                        onDelete={() => onDelete(m.id)}
                    />
                ))}
            </div>
        </div>
    );
}

// ─── A single clickable mapped field ────────────────────────────────────
function FieldBox({
    placed,
    active,
    edited,
    value,
    inert,
    onActivate,
    onDeactivate,
    onStage,
    onRevert,
}: {
    placed: PlacedField;
    active: boolean;
    edited: boolean;
    value: string;
    inert: boolean;
    onActivate: () => void;
    onDeactivate: () => void;
    onStage: (value: string | null) => void;
    onRevert: () => void;
}) {
    const { field } = placed;
    const toggle = isToggleField(field);
    const on = isTruthy(value);
    const style: React.CSSProperties = {
        left: `${placed.xPct * 100}%`,
        top: `${placed.yPct * 100}%`,
        width: `${placed.wPct * 100}%`,
        height: `${placed.hPct * 100}%`,
        pointerEvents: inert ? "none" : "auto",
    };

    if (toggle) {
        return (
            <button
                type="button"
                title={`${fieldDisplayLabel(field)} — tap to ${on ? "uncheck" : "check"}`}
                style={style}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                    e.stopPropagation();
                    onStage(on ? "false" : "true");
                }}
                className={cn(
                    "absolute flex items-center justify-center rounded-[2px] text-[13px] font-bold",
                    edited ? "bg-sky-200/50 ring-2 ring-sky-500" : "ring-1 ring-sky-400/50 hover:bg-sky-100/40",
                )}
            >
                {on ? <span className="text-black">✓</span> : null}
            </button>
        );
    }

    // Text / date / number field: a clickable box that opens an inline input.
    if (active) {
        return (
            <div className="absolute" style={style} onPointerDown={(e) => e.stopPropagation()}>
                <input
                    autoFocus
                    defaultValue={value}
                    onBlur={(e) => {
                        onStage(e.target.value);
                        onDeactivate();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            onStage((e.target as HTMLInputElement).value);
                            onDeactivate();
                        } else if (e.key === "Escape") {
                            onDeactivate();
                        }
                    }}
                    className="h-full w-full rounded-[2px] bg-white px-1 text-[12px] leading-none text-black outline-none ring-2 ring-sky-500"
                />
            </div>
        );
    }
    return (
        <div
            role="button"
            tabIndex={0}
            title={`${fieldDisplayLabel(field)} — tap to edit`}
            style={style}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
                e.stopPropagation();
                onActivate();
            }}
            className={cn(
                "absolute flex items-center rounded-[2px] px-1",
                edited ? "bg-sky-200/40 ring-2 ring-sky-500" : "ring-1 ring-sky-400/50 hover:bg-sky-100/40",
            )}
        >
            {edited && (
                <>
                    <span className="truncate text-[12px] leading-none text-black">{value}</span>
                    <button
                        type="button"
                        title="Revert to original"
                        aria-label="Revert field"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.stopPropagation();
                            onRevert();
                        }}
                        className="ml-auto flex h-3.5 w-3.5 flex-none items-center justify-center rounded-full bg-sky-700 text-white"
                    >
                        <RotateCcw className="h-2.5 w-2.5" />
                    </button>
                </>
            )}
        </div>
    );
}

// ─── A single draggable/resizable mark ──────────────────────────────────
function MarkBox({
    mark,
    selected,
    layerRef,
    onSelect,
    onUpdate,
    onDelete,
}: {
    mark: Mark;
    selected: boolean;
    layerRef: React.RefObject<HTMLDivElement>;
    onSelect: () => void;
    onUpdate: (patch: Partial<Mark>) => void;
    onDelete: () => void;
}) {
    const drag = useRef<{ mode: "move" | "resize"; startX: number; startY: number; m: Mark } | null>(null);

    const onPointerMove = useCallback(
        (e: PointerEvent) => {
            const d = drag.current;
            const rect = layerRef.current?.getBoundingClientRect();
            if (!d || !rect) return;
            const dx = (e.clientX - d.startX) / rect.width;
            const dy = (e.clientY - d.startY) / rect.height;
            if (d.mode === "move") {
                onUpdate({
                    xPct: Math.max(0, Math.min(1 - d.m.wPct, d.m.xPct + dx)),
                    yPct: Math.max(0, Math.min(1 - d.m.hPct, d.m.yPct + dy)),
                });
            } else {
                onUpdate({
                    wPct: Math.max(0.02, Math.min(1 - d.m.xPct, d.m.wPct + dx)),
                    hPct: Math.max(0.012, Math.min(1 - d.m.yPct, d.m.hPct + dy)),
                });
            }
        },
        [layerRef, onUpdate],
    );

    const endDrag = useCallback(() => {
        drag.current = null;
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", endDrag);
    }, [onPointerMove]);

    const beginDrag = (mode: "move" | "resize") => (e: React.PointerEvent) => {
        e.stopPropagation();
        e.preventDefault();
        onSelect();
        drag.current = { mode, startX: e.clientX, startY: e.clientY, m: mark };
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", endDrag);
    };

    const style: React.CSSProperties = {
        left: `${mark.xPct * 100}%`,
        top: `${mark.yPct * 100}%`,
        width: `${mark.wPct * 100}%`,
        height: `${mark.hPct * 100}%`,
    };

    return (
        <div
            className={cn(
                "absolute box-border",
                mark.type === "circle" ? "rounded-full border-2 border-black/70" : "rounded-[2px]",
                mark.type === "whiteout" ? "bg-white" : "",
                selected ? "ring-2 ring-primary" : "ring-1 ring-primary/40",
            )}
            style={style}
            // Tap the mark to select it — that reveals the move grip, the delete
            // button, and the resize handle (below).
            onPointerDown={(e) => {
                e.stopPropagation();
                onSelect();
            }}
        >
            {mark.type === "text" && (
                <input
                    value={mark.value ?? ""}
                    onChange={(e) => onUpdate({ value: e.target.value })}
                    onPointerDown={(e) => {
                        e.stopPropagation();
                        onSelect();
                    }}
                    placeholder="type…"
                    className="h-full w-full bg-transparent px-1 text-[12px] leading-none text-black outline-none"
                />
            )}
            {mark.type === "checkbox" && (
                <div className="flex h-full w-full items-center justify-center text-[13px] font-bold text-black">✓</div>
            )}

            {selected && (
                <>
                    {/* Control bar floating just above the mark: a drag GRIP to
                        move (the mark body can't be the drag surface — a text
                        input fills it), and a clear DELETE button. */}
                    <div className="absolute -top-6 left-0 flex items-center gap-1">
                        <div
                            onPointerDown={beginDrag("move")}
                            style={{ touchAction: "none" }}
                            title="Drag to move"
                            className="flex h-5 cursor-move select-none items-center gap-1 rounded bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground shadow"
                        >
                            <Move className="h-3 w-3" /> move
                        </div>
                        <button
                            type="button"
                            onPointerDown={(e) => {
                                e.stopPropagation();
                                onDelete();
                            }}
                            title="Delete this mark"
                            aria-label="Delete mark"
                            className="flex h-5 w-5 items-center justify-center rounded bg-destructive text-destructive-foreground shadow"
                        >
                            <Trash2 className="h-3 w-3" />
                        </button>
                    </div>
                    {/* Corner resize handle. */}
                    <div
                        onPointerDown={beginDrag("resize")}
                        style={{ touchAction: "none" }}
                        title="Drag to resize"
                        className="absolute -bottom-2 -right-2 h-4 w-4 cursor-se-resize rounded-sm border-2 border-primary bg-white shadow"
                    />
                </>
            )}
        </div>
    );
}
